import { Component } from '@theme/component';
import { debounce, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;
  #touchStartY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    requestAnimationFrame(() => {
      document.documentElement.setAttribute('scroll-lock', '');

      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('keydown', this.#handleKeyDown);

      // Important: bind interaction listeners to the dialog itself,
      // because showModal() places it in the browser top layer.
      dialog.addEventListener('click', this.#handleClick);
      dialog.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
      dialog.addEventListener('touchmove', this.#handleTouchMove, { passive: false });
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('keydown', this.#handleKeyDown);
    dialog.removeEventListener('click', this.#handleClick);
    dialog.removeEventListener('touchstart', this.#handleTouchStart);
    dialog.removeEventListener('touchmove', this.#handleTouchMove);

    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.documentElement.removeAttribute('scroll-lock');

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Close only when the backdrop itself is clicked.
   *
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const { dialog } = this.refs;

    if (event.target === dialog) {
      this.closeDialog();
    }
  };

  /**
   * Store touch start position.
   *
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    if (!event.touches.length) return;
    this.#touchStartY = event.touches[0].clientY;
  };

  /**
   * Prevent iOS Safari rubber-band scroll from escaping the dialog.
   *
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const scrollContainer = target?.closest('.cart-drawer__content, .drawer__content');

    // If gesture is outside the intended scroll area, block it.
    if (!scrollContainer) {
      event.preventDefault();
      return;
    }

    const currentY = event.touches[0].clientY;
    const deltaY = currentY - this.#touchStartY;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
    const hasScrollableContent = scrollHeight > clientHeight;
    const isAtTop = scrollTop <= 0;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

    // No internal scroll available: always trap the gesture.
    if (!hasScrollableContent) {
      event.preventDefault();
      return;
    }

    // Pulling down at top: block rubber-band escape.
    if (isAtTop && deltaY > 0) {
      event.preventDefault();
      return;
    }

    // Pulling up at bottom: block rubber-band escape.
    if (isAtBottom && deltaY < 0) {
      event.preventDefault();
    }
  };

  /**
   * Closes the dialog when the user presses escape.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  };

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number}
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number}
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) {
  customElements.define('dialog-component', DialogComponent);
}

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);